import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';

import type { FaceMove, Smile } from './Avatar';
import { prefersReducedMotion } from './faceTap';

/**
 * Faces in a list are alive, and never in step.
 *
 * The owner's ask of 2026-09-04: on «Хор» and in the arrivals feed the faces
 * must feel alive, «не в один момент у всех». Forty faces blinking on the same
 * frame is not liveliness, it is a strobe - and it is what any animation
 * declared once in a stylesheet does, because every element that gets the rule
 * gets the same start time.
 *
 * So there are two layers, and they answer two different halves of it.
 *
 * **The phase** is per face and permanent: a stable hash of the member's id
 * gives each one its own delay and its own period, so two identical faces side
 * by side never blink together and neither moves when the list re-renders or
 * scrolls. A random number would change on every render and make the list
 * twitch; the id is the one thing about a row that does not.
 *
 * **The scheduler** is per list and not per face. Every 6-14 seconds exactly
 * one visible face makes one gesture suited to its mood, and never two at once.
 * A face that moves whenever it likes is a list that never settles; one that
 * moves on a shared clock is a wave. This is the middle: the eye catches
 * something moving, looks, and finds a person.
 *
 * Not to be confused with `src/liveness.ts`, which keeps *data* fresh. This
 * file moves faces and asks the server nothing.
 */

/** One row's face, as the scheduler needs to see it. */
export type LiveFace = {
  id: number;
  smile: Smile;
  away: boolean;
};

/**
 * A stable per-member hash, so a face keeps its phase across renders and
 * scrolls. FNV-1a over the decimal id: cheap, and it spreads neighbouring ids
 * across the range, which consecutive member numbers in one choir need.
 */
export function hashId(id: number | string): number {
  let hash = 2166136261;
  for (const ch of String(id)) {
    hash ^= ch.charCodeAt(0);
    hash = Math.imul(hash, 16777619) >>> 0;
  }
  return hash;
}

/**
 * The inline style that gives one face its own clock, or undefined for the
 * three steps that stand still.
 *
 * The delay is negative on purpose: a face starts mid-cycle rather than at the
 * beginning of one, so a list that has just been drawn is already in motion
 * instead of blinking in unison a second later.
 */
export function livenessPhase(id: number | string, smile: Smile): CSSProperties | undefined {
  if (smile !== 'wide' && smile !== 'sad') return undefined;
  const hash = hashId(id);
  const base = smile === 'wide' ? 4500 : 5500;
  return {
    animationDelay: `-${hash % 5000}ms`,
    animationDuration: `${base + ((hash >>> 4) % 2000)}ms`,
  };
}

/** What one face does when the scheduler picks it, by mood. */
export function gestureFor(smile: Smile): FaceMove | null {
  if (smile === 'wide') return 'hop-soft';
  if (smile === 'smile' || smile === 'half') return 'squash';
  if (smile === 'flat') return 'blink-once';
  // The sad face keeps its sigh and is not prodded into a gesture on top of it.
  return null;
}

/** How long a scroll keeps the list still afterwards. */
const AFTER_SCROLL_MS = 400;

/**
 * One scheduler for one mounted list.
 *
 * `visible()` hands back the faces on screen; `play` is given the face to move
 * and the gesture. Returns the stop function.
 *
 * Nothing runs at all under `prefers-reduced-motion`: somebody who asked for
 * less movement did not ask for a slower wave, which is the same rule
 * `index.css` states for the whole app.
 */
export function startGestures(
  visible: () => LiveFace[],
  play: (id: number, move: FaceMove) => void,
): () => void {
  if (prefersReducedMotion()) return () => {};
  let lastScroll = 0;
  const onScroll = () => {
    lastScroll = Date.now();
  };
  // Captured, because the list scrolls inside the page rather than the window
  // on some of these screens and a bubbled scroll event does not exist.
  window.addEventListener('scroll', onScroll, { passive: true, capture: true });
  let timer = 0;
  const tick = () => {
    // Booked before the work, so a tick that decides to do nothing still keeps
    // the clock: the list does not go quiet for ever because a scroll happened.
    timer = window.setTimeout(tick, 6000 + Math.random() * 8000);
    if (document.hidden || Date.now() - lastScroll < AFTER_SCROLL_MS) return;
    const pool = visible().filter((face) => !face.away && gestureFor(face.smile) !== null);
    if (pool.length === 0) return;
    // The widest smiles twice as likely: they are the ones with something to
    // show, and their gesture is the one worth catching out of the corner of an
    // eye.
    const weighted = pool.flatMap((face) => (face.smile === 'wide' ? [face, face] : [face]));
    const pick = weighted[Math.floor(Math.random() * weighted.length)];
    play(pick.id, gestureFor(pick.smile) as FaceMove);
  };
  // The first one comes sooner than the rest: a list that stands perfectly
  // still for a quarter of a minute after it is drawn has already been read.
  timer = window.setTimeout(tick, 3000 + Math.random() * 4000);
  return () => {
    window.clearTimeout(timer);
    window.removeEventListener('scroll', onScroll, { capture: true });
  };
}

/** The face moving right now, and when it started — the `key` is made of both. */
export type Gesture = { id: number; move: FaceMove; at: number };

/**
 * The scheduler as one list's hook.
 *
 * `seen` is a ref callback per row: it is what tells the observer which faces
 * are on screen, so a roster of forty does not spend its gestures on the
 * thirty-two below the fold. Where there is no `IntersectionObserver` — the
 * test runner has none — every face in the list counts as visible, which is
 * the safe end of the mistake: something moves rather than nothing.
 */
export function useGestures(faces: LiveFace[]): {
  seen: (id: number) => (element: HTMLElement | null) => void;
  gesture: Gesture | null;
} {
  const [gesture, setGesture] = useState<Gesture | null>(null);
  // Refs and not state: the visible set changes on every scroll, and a list
  // that re-rendered for it would fight the scrolling it is watching.
  const onScreen = useRef(new Set<number>());
  const elements = useRef(new Map<number, HTMLElement>());
  const observer = useRef<IntersectionObserver | null>(null);
  const latest = useRef(faces);
  latest.current = faces;

  // Built on the first row that asks to be watched rather than in an effect:
  // the ref callbacks run before effects do, and an observer that arrives after
  // them would have been handed every element and told about none.
  if (observer.current === null && typeof IntersectionObserver === 'function') {
    observer.current = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const id = Number((entry.target as HTMLElement).dataset.faceId);
        if (entry.isIntersecting) onScreen.current.add(id);
        else onScreen.current.delete(id);
      }
    });
  }

  const seen = useCallback(
    (id: number) => (element: HTMLElement | null) => {
      const held = elements.current.get(id);
      if (held !== undefined && held !== element) {
        observer.current?.unobserve(held);
        elements.current.delete(id);
        onScreen.current.delete(id);
      }
      if (element === null) return;
      element.dataset.faceId = String(id);
      elements.current.set(id, element);
      observer.current?.observe(element);
    },
    [],
  );

  useEffect(() => {
    const stop = startGestures(
      () =>
        latest.current.filter((face) => observer.current === null || onScreen.current.has(face.id)),
      (id, move) => setGesture({ id, move, at: Date.now() }),
    );
    // The observer outlives the effect on purpose: React's development remount
    // runs this twice, and disconnecting one the ref callbacks are still
    // feeding would leave the second run watching nothing.
    return stop;
  }, []);

  return { seen, gesture };
}
